/**
 * The closed noun dictionary (PROJECT-KNOWLEDGE.md §Terminology law) and the state label maps.
 *
 * No component inlines a noun this dictionary holds, and no component inlines a state's words.
 * A tenant module overrides entries through `TenantModule.labels` (Phase 5); `mergeLabels` is the
 * only merge. `Labels` is closed: an unknown key in an override is a type error.
 */
import type { Delivery, PlanningRun, Shipment, Upload } from "../api/client";

/** The closed dictionary. Keys are exactly PROJECT-KNOWLEDGE.md §Terminology law's list. */
export interface Labels {
  site: string;
  sites: string;
  product: string;
  products: string;
  laneRate: string;
  laneRates: string;
  mode: string;
  modes: string;
  carrier: string;
  carriers: string;
  closure: string;
  closures: string;
  priorityPolicy: string;
  priorityPolicies: string;
  transferRequest: string;
  transferRequests: string;
  bookedLoad: string;
  bookedLoads: string;
  reservedCapacity: string;
  shipment: string;
  shipments: string;
  planningRun: string;
  planningRuns: string;
  experiments: string;
  currentPlan: string;
  load: string;
  loads: string;
  delivery: string;
  deliveries: string;
  siteWorkspace: string;
  laneWorkspace: string;
}

export type LabelKey = keyof Labels;

/** Core's defaults — the contract vocabulary. */
export const coreLabels: Labels = {
  site: "Site",
  sites: "Sites",
  product: "Product",
  products: "Products",
  laneRate: "Lane rate",
  laneRates: "Lane rates",
  mode: "Mode",
  modes: "Modes",
  carrier: "Carrier",
  carriers: "Carriers",
  closure: "Closure",
  closures: "Closures",
  priorityPolicy: "Priority policy",
  priorityPolicies: "Priority policies",
  transferRequest: "Transfer request",
  transferRequests: "Transfer requests",
  bookedLoad: "Booked load",
  bookedLoads: "Booked loads",
  reservedCapacity: "Reserved capacity",
  shipment: "Shipment",
  shipments: "Shipments",
  planningRun: "Planning run",
  planningRuns: "Planning runs",
  experiments: "Experiments",
  currentPlan: "Current plan",
  load: "Load",
  loads: "Loads",
  delivery: "Feed delivery",
  deliveries: "Data feed log",
  siteWorkspace: "Site workspace",
  laneWorkspace: "Lane workspace",
};

/** Later overrides win. Undefined entries never blank a core word. */
export function mergeLabels(core: Labels, overrides: Partial<Labels>[]): Labels {
  const merged: Labels = { ...core };
  for (const override of overrides) {
    for (const [key, value] of Object.entries(override) as [LabelKey, string | undefined][]) {
      if (value !== undefined) merged[key] = value;
    }
  }
  return merged;
}

/* -------------------------------------------------------------------------- */
/* State labels — one map per StateChip family.                               */
/* -------------------------------------------------------------------------- */

export type RunState = PlanningRun["state"];
export type UploadState = Upload["state"];
export type DeliveryState = Delivery["state"];
export type ShipmentType = Shipment["shipmentType"];
export type SalesOutcome = "max_of_both" | "orders_only" | "forecast_only" | "blocked";
export type OverrideState = "override" | "overtaken";
export type PublicationState = "draft" | "published";
export type FindingSeverity = "high" | "medium" | "low";
export type Disposition = "open" | "acknowledged" | "snoozed";
export type AuditAction = "create" | "update" | "delete" | "upload" | "feed_upsert";

export const stateLabels = {
  run: {
    draft: "Draft",
    preparing: "Preparing",
    package_ready: "Package ready",
    dispatched: "Dispatched",
    running: "Running",
    succeeded: "Succeeded",
    failed: "Failed",
    cancelled: "Cancelled",
  } satisfies Record<RunState, string>,
  upload: {
    received: "Received",
    validating: "Validating",
    validated: "Validated",
    invalid: "Has issues",
    applying: "Applying",
    applied: "Applied",
    failed: "Failed",
  } satisfies Record<UploadState, string>,
  delivery: {
    received: "Received",
    processing: "Processing",
    processed: "Processed",
    rejected: "Rejected",
    failed: "Failed",
  } satisfies Record<DeliveryState, string>,
  outcome: {
    max_of_both: "Both feeds",
    orders_only: "Orders only",
    forecast_only: "Forecast only",
    blocked: "Blocked",
  } satisfies Record<SalesOutcome, string>,
  override: {
    override: "Override",
    overtaken: "Overtaken",
  } satisfies Record<OverrideState, string>,
  publication: {
    draft: "Draft",
    published: "Published",
  } satisfies Record<PublicationState, string>,
  shipmentType: {
    solved: "Solved",
    sto_fixed: "Fixed (STO)",
    committed_fixed: "Fixed (committed)",
    carrier_minimum: "Fixed (carrier minimum)",
  } satisfies Record<ShipmentType, string>,
  findingSeverity: {
    high: "High",
    medium: "Medium",
    low: "Low",
  } satisfies Record<FindingSeverity, string>,
  disposition: {
    open: "Open",
    acknowledged: "Acknowledged",
    snoozed: "Snoozed",
  } satisfies Record<Disposition, string>,
  auditAction: {
    create: "Created",
    update: "Updated",
    delete: "Deleted",
    upload: "Uploaded",
    feed_upsert: "Feed update",
  } satisfies Record<AuditAction, string>,
} as const;

/** The StateChip families. */
export type StateFamily = keyof typeof stateLabels;

/** The label for a value in a family; the raw value when a family gains a value we don't know. */
export function stateLabel(family: StateFamily, value: string): string {
  const map = stateLabels[family] as Record<string, string | undefined>;
  return map[value] ?? value;
}

/**
 * The words `ProblemAlert` renders, one entry per `ProblemKind`. Copy lives here, never inline
 * in the renderer. `detail`-carrying kinds (`conflict`, `not_found`, `unsupported_upload`) take
 * the Problem's own `detail` as the whole message; the fallback is used when it is absent.
 */
export const problemCopy = {
  unauthenticated: {
    title: "Your session has ended",
    body: "Sign in again to continue.",
    action: "Sign in again",
  },
  forbidden: {
    title: "You don't have permission",
    /** `{permission}` is replaced with the Problem's `requiredPermission`. */
    body: "You don't have {permission} on this tenant.",
  },
  not_found: { title: "Not found", body: "That record no longer exists." },
  /* Both show `detail` verbatim where the server sent one — it names the run and the reason. */
  derived_pending: {
    title: "Still being computed",
    body: "This run's findings are being computed. They will appear here on their own.",
  },
  derived_unavailable: {
    title: "Not available for this run",
    body: "This run's findings were not computed, and asking again will not produce them.",
  },
  tenant_not_resolved: {
    title: "This deployment is not configured for a tenant",
    body: "The API did not resolve a tenant for this host. Check VITE_API_BASE_URL and the deployment's tenant mapping.",
  },
  conflict: { title: "That didn't go through", body: "Something else changed first." },
  validation: { title: "Check the highlighted fields", body: "" },
  unsupported_upload: {
    title: "That file type isn't supported",
    body: "Upload a CSV or XLSX file.",
  },
  server: { title: "Something went wrong", body: "The service could not complete the request." },
  network: { title: "Something went wrong", body: "The service could not be reached." },
} as const;

export const problemActions = { retry: "Retry" } as const;
